
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { BehaviorSubject, concat, debounceTime, first, last, Subject, takeUntil, tap } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { Project, ProjectPreview, ProjectStatus } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { FlowService } from 'src/app/services/api/flows/flow.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

@Component({
  selector: 'app-projects',
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    TranslateModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatChipsModule,
    MatIconModule,
    MatSortModule,
    MatPaginatorModule,
    SpinnerComponent,
    MatToolbarModule,
    TimeFormatPipe,
    SearchBarComponent,
    ButtonComponent,
    NoDataComponent,
    ChipComponent,
    MatTooltipModule
  ],
  standalone: true,
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})

export class ProjectsComponent implements OnInit, AfterViewInit, OnDestroy {
  // Services
  private projectService = inject(ProjectService);
  private flowService = inject(FlowService);
  private auth = inject(AuthService)
  private userService = inject(UserService);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private pop = inject(Pop);

  // Components API
  @ViewChild('projectPaginator') projectPaginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Variables
  user!: User;
  paginator: any = {
    pageIndex: 0,
    pageSize: 10
  };
  paginatorSbj: BehaviorSubject<any> = new BehaviorSubject(this.paginator);
  loading = true;
  projects: Project[] = [];
  projectsCount: number = 0;
  projectsData: MatTableDataSource<Project> = new MatTableDataSource();
  projectsColumns: string[] = ['fav', 'name', 'reference', 'status', 'units', 'date', 'actions'];
  isFavourite = false;
  showArchived: boolean = false;
  statusSub: any[] = [];
  searchText: string = "";
  validSearchText: string = "";
  loadingTimeout: any;
  showList = false;
  defaultImage = "/img/default_project.png";;
  hasAdminAccess: boolean = false;
  isDark: boolean = false;
  destroy$ = new Subject<void>();
  processing: Record<string, boolean> = {};
  canManageProject: boolean = false;
  requestCancel$ = new Subject<void>();
  searchSubject$ = new Subject<string>();
  lastEmptySearch: string = "";

  // Signals
  readonly projectsVisited = this.auth.projectsVisited;

  // Computed
  readonly favouriteProjects = computed(() => this.auth.currentUser().favouriteProjects ?? []);

  constructor() {
    this.user = this.auth.currentUser();
    this.hasAdminAccess = this.user.role?.name == "admin" || this.user.role?.name == "dev";
    this.canManageProject = this.hasAdminAccess || this.user.role?.name == "userAdmin";

    const settings = this.user.settings;
    if (settings?.projectsView) this.showList = settings.projectsView === 'list';
    if (settings?.showArchivedProjects !== undefined) this.showArchived = settings.showArchivedProjects;
  }

  ngOnInit() {
    this.loading = true;
  }

  ngAfterViewInit(): void {
    this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(text => {
      if (text !== this.validSearchText) {
        if (this.lastEmptySearch && text.startsWith(this.lastEmptySearch)) return;
        this.validSearchText = text;
        this.projectPaginator.pageIndex = 0;
        this.loadProjects();
      }
    });

    Promise.resolve().then(() => {
      this.loadProjects();
    });
  }

  ngOnDestroy(): void {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.statusSub.forEach(sub => sub.unsubscribe());

    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    this.searchSubject$.next(text);
  }

  loadProjects() {
    this.requestCancel$.next();
    this.loading = true;

    const query: any = {
      owner: this.user.id,
      author: this.user.id,
      users: this.user.id,
      offset: this.projectPaginator ? this.projectPaginator.pageIndex * this.projectPaginator.pageSize : 0,
      limit: this.projectPaginator ? this.projectPaginator.pageSize : 10,
      archived: this.showArchived,
      models: true
    };

    const searchText = this.validSearchText && this.validSearchText.trim() !== '' ? this.validSearchText.trim() : "";

    this.projectService.searchAndCountProjects(searchText, query)
      .pipe(takeUntil(this.requestCancel$))
      .subscribe({
        next: (res: { results: Project[]; total: number }) => {
          this.lastEmptySearch = (res.total === 0 && searchText) ? searchText : "";
          this.projects = res.results;
          this.projectsCount = res.total;
          this.projects.forEach(project => {
            this.processing[project.id] = this.processing[project.id] || false;
          });

          this.projects.sort((a, b) => moment(a.createdDate).diff(b.createdDate) < 0 ? 1 : -1);

          if (this.projectPaginator) {
            this.projectPaginator.length = res.total;
          }

          this.handleProjects();
        },
        error: (err) => {
          this.loading = false;
          console.error('❌ Erreur lors de la recherche des projets:', err);
        }
      });
  }

  handleProjects() {
    if (this.projects.length === 0) {
      this.setMatTableData();
      this.loading = false;
      return;
    }

    this.projects.forEach((project: Project) => {
      if (this.favouriteProjects().includes(project.id))
        project.favourite = true;

      project.createdDate = project.createdAt || project.createdDate;
    })

    this.projects.sort((a, b) => moment(a.createdDate).diff(b.createdDate) < 0 ? 1 : -1);

    const databases = this.projects.map(p => p.database).filter(Boolean);

    if (databases.length) {
      this.projectService.getStatus(databases.join(',')).pipe(first(), takeUntil(this.requestCancel$), takeUntil(this.destroy$)).subscribe({
        next: (status: ProjectStatus[]) => {
          this.projects.forEach(p => {
            p.status = status.find(s => s.database == p.database)
          })

          this.setMatTableData();
        },
        error: (err) => console.error(err)
      })
    }

    const previewDatabases = this.projects.filter(p => !p.picture && p.database).map(p => p.database);

    if (!previewDatabases.length) {
      this.applyThumbnails([]);
      return;
    }

    this.projectService.getPreviews(previewDatabases.join(','))
      .pipe(first(), takeUntil(this.requestCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (previews: ProjectPreview[]) => this.applyThumbnails(previews ?? []),
        error: (err) => {
          console.error(err);
          this.applyThumbnails([]);
        }
      })
  }

  applyThumbnails(previews: ProjectPreview[]) {
    this.projects.forEach(p => {
      if (p.picture) {
        p.thumbnail = this.projectService.getProjectImageUrl(p.picture, 'picture');
      } else {
        let preview = previews.find((s) => s.database == p.database);

        p.thumbnail = preview && preview.image
          ? this.projectService.getProjectImageUrl(preview.image, 'thumbnail')
          : this.defaultImage;
      }

      p.loading = false;
    })

    this.setMatTableData();
    this.loading = false;
  }

  setMatTableData() {
    this.projectsData.data = this.projects;
  }

  setCurrentProject(projectId: string) {
    this.projectService.getProjectById(projectId).pipe(takeUntil(this.destroy$)).pipe(first()).subscribe((project: Project) => {
      concat(
        this.projectService.getStatus(project.database).pipe(
          first(),
          tap(result => {
            project.status = result[0];
          })),

        this.projectService.getActivity({ databases: project.database }).pipe(
          first(),
          tap(result => {
            project.activity = result;
          }))
      ).pipe(takeUntil(this.destroy$)).pipe(last()).subscribe(() => {
        this.projectService.setCurrentProject(project);
      });
    })
  }

  selectCurrentProject(projectId: string) {
    this.statusSub.forEach(sub => sub.unsubscribe())
    this.setCurrentProject(projectId)

    this.userService.setCurrentProject(projectId).pipe(takeUntil(this.destroy$)).pipe(first()).subscribe((res: any) => {
      if (!res) return;

      let visited = [...this.projectsVisited()];
      const index = visited.indexOf(projectId);

      if (index > -1)
        visited.splice(index, 1);

      visited.push(projectId);
      this.userService.addVisitedProjects(visited);
      this.router.navigate(['/project/dashboard']);
    }, err => {
      console.error(err);
    })
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;

    if (this.projectsData) {
      this.projectsData.filter = filterValue.trim().toLowerCase();

      if (this.projectsData.paginator) {
        this.projectsData.paginator.firstPage();
      }
    }
  }

  async toggleFavourite(projectId: string) {
    try {
      const isFavourite = this.favouriteProjects().includes(projectId);
      let favs = [...this.favouriteProjects()];

      if (isFavourite) {
        await new Promise((resolve, reject) => {
          this.pop.confirm(this.translate.instant('projects.remove_favorite'), (confirm: any) => {
            if (confirm.ok) {
              favs = favs.filter(id => id !== projectId);
              this.userService.updateFavouriteProjects(favs);
              resolve("ok");
            } else {
              reject("not ok");
            }
          });
        });
      } else {
        favs.push(projectId);
        this.userService.updateFavouriteProjects(favs);
      }

      this.projects.forEach(project => {
        project.favourite = favs.includes(project.id);
      });
    } catch (err) { }
  }

  archiveProject(project: Project) {
    let archived = project.archived ? false : true;

    const onConfirm = () => {
      let query = {
        id: project.id,
        archived
      }

      this.processing[project.id] = true;

      if (archived) {
        const favs = [...this.favouriteProjects()].filter(id => id !== project.id);
        this.userService.updateFavouriteProjects(favs);

        this.projects.forEach(project => {
          project.favourite = favs.includes(project.id);
        });
      }

      this.projectService.setProject(query).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          if (/document updated/i.test(res.message)) {
            this.projects.forEach(p => {
              if (p.id == project.id) {
                p.archived = archived;

                if (this.user?.currentProject == project.id) {
                  this.projectService.setCurrentProject(p);
                }
              }
            });

            this.pop.toast(this.translate.instant(`projects.archive.${!archived ? 'undo_' : ''}success`), 'success', 5000, 'check_circle');
            this.processing[project.id] = false;
          } else {
            this.pop.warning(this.translate.instant(`projects.archive.${!archived ? 'undo_' : ''}failure`));
            this.processing[project.id] = false;
          }
        },
        error: (err) => {
          this.pop.error(this.translate.instant(`projects.archive.${!archived ? 'undo_' : ''}error`, { errorCode: err.status }));
          this.processing[project.id] = false;
        }
      });
    }

    this.pop.confirm(this.translate.instant(`projects.archive.${!archived ? 'undo_' : ''}confirm`), (data: any) => {
      if (!data.dismissed)
        onConfirm()
    });
  }

  listArchived(event: boolean) {
    this.showArchived = event;
    this.projectPaginator.pageIndex = 0;

    this.loadProjects();

    this.userService.updatePersonalization({ showArchivedProjects: event })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: err => console.error('❌ Erreur lors de la sauvegarde de la personnalisation:', err) });
  }

  toggleShowList(bool: boolean) {
    this.showList = bool;

    this.userService.updatePersonalization({ projectsView: bool ? 'list' : 'card' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: err => console.error('❌ Erreur lors de la sauvegarde de la personnalisation:', err) });
  }

  public handleMissingImage(event: Event) {
    (event.target as HTMLImageElement).src = this.defaultImage;
  }

  //TODO
  reportsEnabling() {
    // const modal = this.dialog.open(ReportsEnablingModal, {
    //   width: '450px',
    //   data: { user: this.user }
    // })

    // modal.afterClosed().subscribe(() => {
    // })
  }

  showProjectName(name: string) {
    return name.length >= 50 ? name.slice(0, 50).trim() + "..." : name;
  }

  addProject() {
    this.router.navigate(['/project-detail']);
  }

  alertFrozen() {
    this.pop.warning(this.translate.instant(`projects.frozen`));
  }

  handlePageEvent(e: PageEvent) {
    this.loadProjects();
  }
}
